import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import {
  DECISION_LABEL, decidedNotice, offerStillOpen, STALE_DECISION_NOTICE,
  type CommandTurn, type PlanDecision, type PlanDecisionOffer,
} from '../lib/command-chat';
import { HeldDraftChoices } from './HeldDraftChoices';
import type { PlanOutcome } from '../lib/outcome';
import { usePlanDecision } from '../lib/mutations';
import { CHAT_MIN_PX, clampChatHeight, recallChatHeight, rememberChatHeight } from '../lib/chat-height';
import { browserStore } from '../lib/sections';
import {
  isDiscussionMode,
  type CommandDiscussionMode,
  type SharedTimelineItem,
} from '../lib/shared-command-timeline';
import { MainCommandConversationTabs, type MainCommandOrganizerTool } from './MainCommandConversationTabs';
import {
  archiveLabel,
  commandTurnsInArchiveRange,
  mainCommandContextMeterLabel,
  timelineInArchiveRange,
  type MainCommandChatArchive,
} from '../lib/main-command-chat-archive';
import type { MainCommandConversationController } from '../lib/main-command-controller';
import type { WorkerRequestBlock } from '../lib/worker-request-blocks';
import { WorkerRequestBlocks } from './WorkerRequestBlocks';
import { ChatText } from './ChatText';
import { replyQuotes, replyRefForItem, replyRefKey, type MainCommandReplyRef } from '../lib/main-command-reply';
import { MessageTimestamp } from './MessageTimestamp';
import { MainCommandReceipt } from './MainCommandReceipt';
import { DisclosureChevron } from './DisclosureChevron';
import { ControlReceiptNote } from './ControlReceiptNote';
import { STEERING_MESSAGE, type ControlReceiptView } from '../lib/control-receipt';
import type { MainCommandDirectRecipient } from '../lib/main-command-recipients';
import '../chat.css';

/** @deprecated Prefer commandTurnsInArchiveRange — kept for existing call sites. */
export function commandTurnsAfterClear(turns: readonly CommandTurn[], clearThroughSeq: number | null): readonly CommandTurn[] {
  return commandTurnsInArchiveRange(turns, clearThroughSeq, null);
}

export function latestVisibleCommandSeq(turns: readonly CommandTurn[]): number | null {
  let latest: number | null = null;
  for (const turn of turns) {
    if (turn.mine && turn.seq !== null && (latest === null || turn.seq > latest)) latest = turn.seq;
  }
  return latest;
}

/**
 * Hide items at or before the durable archive boundary (#415).
 * Covers command-authority groups AND Observer discussion.
 */
export function timelineAfterCommandClear(
  items: readonly SharedTimelineItem[],
  clearThroughSeq: number | null,
  archivedAt: string | null = null,
): readonly SharedTimelineItem[] {
  if (clearThroughSeq === null && archivedAt === null) return items;
  const boundary: MainCommandChatArchive = {
    id: 'local',
    orgId: '',
    archivedAt: archivedAt ?? '1970-01-01T00:00:00.000Z',
    throughCommandSeq: clearThroughSeq,
  };
  return timelineInArchiveRange(items, boundary, null);
}

/**
 * The newest owner turn onward: changes whenever the latest exchange gains a
 * reply, a same-id answer is rewritten, or its recipient statuses hydrate —
 * each of which grows the transcript without changing its length.
 */
export function currentExchangeKey(items: readonly SharedTimelineItem[]): string {
  let start = items.length - 1;
  while (start > 0 && !items[start]!.mine) start -= 1;
  return items.slice(Math.max(0, start)).map((item) => [
    item.id,
    item.text.length,
    (item.recipientStatuses ?? []).map((status) => status.state).join(','),
  ].join(':')).join('|');
}

/**
 * The status pill drawn beside a byline. A reply already shows by being there, so
 * the routine ANSWERED pill on it is left off; every other state (queued, failed,
 * waiting on you, a control's result) still shows. The timeline itself is unchanged.
 */
export function shownStatusLabel(item: Pick<SharedTimelineItem, 'statusLabel' | 'mine' | 'commandTurn'>): string | null {
  const label = item.statusLabel;
  if (label === null || label.trim() === '') return null;
  if (!item.mine && item.commandTurn?.kind === 'answer' && label === 'ANSWERED') return null;
  return label;
}

/**
 * A reply with nothing in it - no text, reason, decision or outcome - would draw
 * a bare byline over an empty box, so it is left out. Owner messages always show.
 */
export function hasShownContent(item: SharedTimelineItem): boolean {
  if (item.mine) return true;
  return item.text.trim() !== ''
    || (item.reason !== null && item.reason !== '')
    || item.offer !== null
    || item.outcome !== null
    || item.commandTurn?.kind === 'plan';
}

/** Command Center conversation; the horizontal seam is the only height control. */
export function CommandChat({
  turns, client, guard, onDecided, action, children, conversationController,
  organizerTool, onOrganizerToolChange,
  discussion, unreadCount = 0, attentionCount = 0, onLeaveDiscussion, onDiscussionViewed,
  managementAttention, discussionActions, recipientBar,
  workerRequestBlocks, afterComposer,
  onDirectReply,
  onReplyToMessage,
  replyRefs,
  controlReceipts,
  controlReceiptActions,
  secondaryControls,
}: {
  turns: readonly CommandTurn[];
  client: SupabaseClient;
  guard: Guard;
  onDecided: () => Promise<void>;
  action?: ReactNode;
  children: ReactNode;
  /** One canonical current/archive controller shared with the floating presentation. */
  conversationController: MainCommandConversationController;
  /** #537's compact tool selection is owned by the room so the mini can open canonical Attention. */
  organizerTool: MainCommandOrganizerTool;
  onOrganizerToolChange: (tool: MainCommandOrganizerTool) => void;
  discussion?: CommandDiscussionMode;
  unreadCount?: number;
  /** Pinned management attention attached to #493's current conversation tab. */
  attentionCount?: number;
  onLeaveDiscussion?: () => void;
  /** Mark the active discussion thread read after the owner viewed it. */
  onDiscussionViewed?: () => void;
  /** The one canonical Management Attention surface, rehosted in the full-width Command viewport. */
  managementAttention?: ReactNode;
  discussionActions?: ReactNode;
  /** Compact Reply-to role selector, kept immediately above the owner composer (#424). */
  recipientBar?: ReactNode;
  /** #561 copy-ready Worker requests per approved plan id. Copy only; no dispatch. */
  workerRequestBlocks?: ReadonlyMap<string, readonly WorkerRequestBlock[]>;
  /** Current operational state below the composer; never part of the durable transcript. */
  afterComposer?: ReactNode;
  /** Select exactly this participant and move focus to the canonical composer. */
  onDirectReply?: (recipient: MainCommandDirectRecipient) => void;
  /** #743: quote this exact message in the composer and reply to its participant. */
  onReplyToMessage?: (item: SharedTimelineItem) => void;
  /** #743: owner message id -> the exact earlier message it replied to. */
  replyRefs?: ReadonlyMap<string, MainCommandReplyRef>;
  /** Receipts for commands pressed in this session, keyed by command id. */
  controlReceipts?: ReadonlyMap<string, ControlReceiptView>;
  /** What each sent message became ("New job for Worker 3"), by command; steering when Brunel did not say. */
  controlReceiptActions?: ReadonlyMap<string, string>;
  /**
   * Route readouts and quick commands. They stay one click away in the collapsed
   * Controls drawer instead of taking conversation space.
   */
  secondaryControls?: ReactNode;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const [store] = useState(() => browserStore());
  const [height, setHeight] = useState<number | null>(() => recallChatHeight(store));
  const {
    archivesLoaded,
    orderedArchives,
    conversation,
    recentChats,
    viewingArchiveId,
    viewingArchive,
    visibleTimeline,
    chatBusy,
    chatError,
    orgId,
    selectConversation,
    startNewChat,
    continueFromArchive,
    startFreshContext,
    freshContextResult,
    continuedFromPreviousChat,
  } = conversationController;
  const [controlsOpen, setControlsOpen] = useState(false);
  const mode = discussion ?? { kind: 'command' as const };
  const discussing = isDiscussionMode(mode);

  // A reply can arrive while the bounded Observer window drops an older row, so
  // the count alone may not change; the latest exchange's own items say it did.
  const latestExchangeKey = currentExchangeKey(visibleTimeline);
  const quotes = useMemo(() => replyQuotes(visibleTimeline), [visibleTimeline]);
  const shownTimeline = useMemo(() => visibleTimeline.filter(hasShownContent), [visibleTimeline]);
  // Selecting a conversation tab brings its latest exchange into view. Choosing
  // the tab that is already open changes no other state, so this count is what
  // makes that click scroll instead of silently doing nothing.
  const [tabSelections, setTabSelections] = useState(0);
  useEffect(() => {
    const box = scroller.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [visibleTimeline.length, latestExchangeKey, discussing, mode.kind === 'discussion' ? mode.threadId : null, viewingArchiveId, tabSelections]);

  useEffect(() => {
    if (discussing) onDiscussionViewed?.();
  }, [onDiscussionViewed, discussing, mode.kind === 'discussion' ? mode.threadId : null]);

  // A finding discussion is the Main Command content surface. Close any other
  // Command view rather than leaving it active over the discussion the owner selected.
  useEffect(() => {
    if (discussing) onOrganizerToolChange(null);
  }, [discussing, mode.kind === 'discussion' ? mode.threadId : null, onOrganizerToolChange]);

  useEffect(() => {
    const normalize = () => {
      const top = scroller.current?.getBoundingClientRect().top;
      if (top === undefined) return;
      setHeight((current) => {
        if (current === null) return current;
        const next = clampChatHeight(current, Math.max(CHAT_MIN_PX, window.innerHeight - top));
        if (next !== current) rememberChatHeight(store, next);
        return next;
      });
    };
    normalize();
    window.addEventListener('resize', normalize);
    return () => window.removeEventListener('resize', normalize);
  }, [store]);

  const dragTo = (clientY: number) => {
    const top = scroller.current?.getBoundingClientRect().top;
    if (top === undefined) return;
    const available = Math.max(CHAT_MIN_PX, window.innerHeight - top);
    const next = clampChatHeight(clientY - top, available);
    setHeight(next);
    rememberChatHeight(store, next);
  };
  const onGripDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragTo(event.clientY);
  };
  const onGripMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) dragTo(event.clientY);
  };
  const nudgeGrip = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowUp' ? -32 : event.key === 'ArrowDown' ? 32 : 0;
    if (step === 0) return;
    event.preventDefault();
    const rect = scroller.current?.getBoundingClientRect();
    const current = height ?? rect?.height ?? CHAT_MIN_PX;
    const available = Math.max(CHAT_MIN_PX, window.innerHeight - (rect?.top ?? 0));
    const next = clampChatHeight(current + step, available);
    setHeight(next);
    rememberChatHeight(store, next);
  };


  return (
    <div className="command-chat-organizer-shell">
      <section
        className={`card chatwindow desk${organizerTool === null && discussing ? ' discussion-mode' : ''}${organizerTool === null && viewingArchive ? ' archive-mode' : ''}`}
        aria-label="Command Center"
        data-chat-mode={mode.kind}
        data-command-view={organizerTool ?? (discussing ? 'findings' : viewingArchive ? 'archive-chat' : 'chat')}
        data-testid="main-command-chat"
        data-archive-loaded={archivesLoaded ? 'true' : 'false'}
        data-archive-count={orderedArchives.length}
      >
        <header className="wintop">
          <h2>Command Center</h2>
          <span className="spacer" />
          {action}
          {conversation !== null && (
            <span
              className="tag"
              data-testid="main-command-context-status"
              title="Overseer context for this chat"
            >
              {mainCommandContextMeterLabel(conversation)}
            </span>
          )}
          {organizerTool === null && viewingArchive === null && (
            <button
              type="button"
              className="ghost chat-controls-toggle"
              data-testid="main-command-controls-toggle"
              aria-expanded={controlsOpen}
              aria-controls="main-command-controls"
              title={controlsOpen ? 'Hide routes, quick commands and context controls' : 'Show routes, quick commands and context controls'}
              onClick={() => setControlsOpen((open) => !open)}
            >
              Controls
              <DisclosureChevron direction={controlsOpen ? 'up' : 'down'} />
            </button>
          )}
        </header>

        {/* Opens right under its button, so what a press shows appears beside it and the next Tab reaches it. */}
        {organizerTool === null && viewingArchive === null && controlsOpen && (
          <div id="main-command-controls" className="chat-controls-drawer" data-testid="main-command-controls"
            role="group" aria-label="Command Center controls">
            {conversation !== null && startFreshContext !== undefined && (
              <div className="chat-controls-row">
                <span className="chat-controls-label">Context</span>
                <button
                  type="button"
                  className="ghost"
                  data-testid="main-command-fresh-context"
                  title="Start a fresh Overseer AI context from Brunel's durable context. This chat, its tasks and Workers stay."
                  disabled={chatBusy || orgId === null}
                  onClick={startFreshContext}
                >
                  Start fresh context
                </button>
              </div>
            )}
            {secondaryControls}
          </div>
        )}

        <MainCommandConversationTabs
          items={recentChats}
          archiveItems={recentChats}
          selectedArchiveId={viewingArchiveId}
          unreadCount={0}
          attentionCount={attentionCount}
          canStartNew={orgId !== null && viewingArchive === null}
          busy={chatBusy}
          activeTool={organizerTool}
          managementAttention={managementAttention}
          onSelect={(item) => {
            onOrganizerToolChange(null);
            selectConversation(item.kind === 'current' ? null : item.id);
            setTabSelections((count) => count + 1);
          }}
          onNewChat={startNewChat}
          onTool={onOrganizerToolChange}
        />

        {organizerTool === null && (
          <>
            {viewingArchive !== null && (
              <div className="command-archive-banner" data-testid="main-command-archive-banner" role="status">
                <div className="stack tight">
                  <strong>Archived chat</strong>
                  <span className="muted">{archiveLabel(viewingArchive)} — read only</span>
                </div>
                <div className="row" style={{ gap: '0.35rem' }}>
                  <button
                    type="button"
                    className="ghost"
                    data-testid={`main-command-continue-${viewingArchive.id}`}
                    title="Start a new chat seeded from this archive"
                    disabled={chatBusy || orgId === null}
                    onClick={() => continueFromArchive(viewingArchive.id)}
                  >
                    Continue from this chat
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    data-testid="main-command-back-current"
                    onClick={() => selectConversation(null)}
                  >
                    Back to current
                  </button>
                </div>
              </div>
            )}

            {discussing && viewingArchive === null && (
              <div className="command-discussion-banner" data-testid="command-discussion-banner" role="status">
                <div className="stack tight">
                  <strong>Discussion</strong>
                  <span className="muted">{mode.title}</span>
                  <small className="muted">
                    {mode.threadId === null
                      ? 'Open a finding to read it and decide what to do with it.'
                      : 'Messages you send here go to the recipients selected below, as in Command. They are not added to this finding.'}
                  </small>
                </div>
                <button type="button" className="ghost" data-testid="leave-discussion"
                  onClick={() => onLeaveDiscussion?.()}>
                  Back to Command
                </button>
              </div>
            )}

            <div className="transcript" ref={scroller} aria-label="What you have asked for"
              style={height === null ? undefined : { height: `${height}px`, maxHeight: 'none' }}>
              {continuedFromPreviousChat != null && continuedFromPreviousChat !== '' && (
                <p data-testid="continued-from-previous-chat">{continuedFromPreviousChat}</p>
              )}
              {shownTimeline.length === 0
                ? <p className="chat-empty">{viewingArchive
                  ? 'This archived conversation has no messages.'
                  : 'No messages yet. Write or dictate a command below; replies appear here.'}</p>
                : shownTimeline.map((item, index) => (
                  <div
                    key={item.id}
                    className={turnClass(item, shownTimeline[index - 1])}
                    data-role={item.role}
                    data-authority={item.authority}
                    data-testid={`shared-turn-${item.role}`}
                  >
                    <span className="byline">
                      {item.replyRecipient && onDirectReply ? (
                        <button type="button" className="participant-byline" title={`Reply to ${item.byline}`}
                          aria-label={`Reply to ${item.byline}`} onClick={() => onDirectReply(item.replyRecipient!)}>
                          {item.byline}
                        </button>
                      ) : item.byline}
                      <MessageTimestamp sentAt={item.sentAt} />
                      {shownStatusLabel(item) !== null && <span className="tag turn-status">{shownStatusLabel(item)}</span>}
                      {item.mine && ((item.targetRoles?.length ?? 0) > 0 || (item.targetWorkerLabels?.length ?? 0) > 0) && (
                        <span className="command-target-badges" aria-label="Reply targets">
                          {item.targetRoles!.map((target) => (
                            <span className="tag" key={target} data-reply-target={target}>
                              {target === 'overseer' ? 'Overseer' : target === 'ux' ? 'UX' : 'Core'}
                            </span>
                          ))}
                          {item.targetWorkerLabels!.map((label, index) => (
                            <span className="tag" key={`worker:${label}:${index}`} data-reply-target="worker">
                              {label}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                    {!item.mine && onReplyToMessage && viewingArchive === null && replyRefForItem(item) !== null && (
                      <button type="button" className="message-reply" title={`Reply to this message from ${item.byline}`}
                        aria-label={`Reply to this message from ${item.byline}`} onClick={() => onReplyToMessage(item)}>
                        ↩ Reply
                      </button>
                    )}
                    <div className={itemBubbleClass(item)}>
                      {item.mine && item.commandTurn !== null && (() => {
                        const ref = replyRefs?.get(item.commandTurn.id);
                        if (ref === undefined) return null;
                        const quote = quotes.get(replyRefKey(ref));
                        return (
                          <div className="reply-quote" data-reply-kind={ref.kind}>
                            <strong>{quote?.author ?? 'Earlier message'}</strong>
                            <span>{quote?.text ?? 'That message is no longer on screen.'}</span>
                          </div>
                        );
                      })()}
                      {item.text.trim() !== '' && <div className="said">{item.mine ? item.text : <ChatText text={item.text} />}</div>}
                      {item.mine && (item.recipientStatuses?.length ?? 0) > 0 && (
                        <MainCommandReceipt statuses={item.recipientStatuses!} />
                      )}
                      {item.mine && item.commandTurn !== null && controlReceipts?.get(item.commandTurn.id) !== undefined && (
                        <ControlReceiptNote view={controlReceipts.get(item.commandTurn.id)!}
                          action={controlReceiptActions?.get(item.commandTurn.id) ?? STEERING_MESSAGE} />
                      )}
                      {item.reason !== null && item.reason !== '' && <span className="because">{item.reason}</span>}
                      {item.offer !== null && viewingArchive === null && (
                        <InlineDecision client={client} guard={guard} turns={turns} offer={item.offer} onDecided={onDecided} />
                      )}
                      {item.outcome !== null && <Outcome outcome={item.outcome} />}
                      {item.commandTurn?.kind === 'plan' && workerRequestBlocks?.has(item.commandTurn.id) && (
                        <WorkerRequestBlocks blocks={workerRequestBlocks.get(item.commandTurn.id)!} />
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {chatError !== null && <p className="muted" role="alert" data-testid="main-command-archive-error">{chatError}</p>}
            {freshContextResult != null && (
              <p className="muted" role={freshContextResult.ok ? 'status' : 'alert'} data-testid="main-command-fresh-context-result">
                {freshContextResult.message}
              </p>
            )}

            {discussing && viewingArchive === null && discussionActions !== undefined && discussionActions !== null && (
              <div className="command-discussion-actions" data-testid="command-discussion-actions">
                {discussionActions}
              </div>
            )}

            {viewingArchive === null && recipientBar}

            <div className="grip" role="separator" aria-orientation="horizontal"
              aria-label="Resize conversation" title="Drag to make the conversation taller or shorter"
              tabIndex={0} onPointerDown={onGripDown} onPointerMove={onGripMove} onKeyDown={nudgeGrip} />
            {viewingArchive === null
              ? <><div className="saybox">{children}</div>{afterComposer}</>
              : (
                <div className="saybox archive-readonly" data-testid="main-command-archive-readonly">
                  <p className="muted">This archived conversation is read-only. Return to current to send new messages.</p>
                </div>
              )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The decision, where the owner is already standing. The plan's summary and its
 * tasks are readable here first - nothing is approved unread - and the decision
 * itself goes out through the same guarded write the Plans drawer uses, so who
 * may approve is unchanged. What is offered is decided in command-chat.ts; this
 * only draws it.
 *
 * A held draft that can be answered shows its choices first, so the button that
 * drops the request is never the only thing to press while a choice exists. Try
 * again, where a second check can clear the hold, and the drop each have what
 * they do said beside them.
 */
export function InlineDecision({ client, guard, turns, offer, onDecided }: {
  client: SupabaseClient; guard: Guard; turns: readonly CommandTurn[];
  offer: PlanDecisionOffer; onDecided: () => Promise<void>;
}) {
  const { decide, busy, error, choices: refusedChoices } = usePlanDecision(client);
  const [notice, setNotice] = useState<string | null>(null);
  const act = (decision: PlanDecision, workerNumber?: number) => void guard('post_owner_command', async () => {
    const open = offerStillOpen(turns, offer.planId);
    if (open === null) {
      setNotice(STALE_DECISION_NOTICE);
      return;
    }
    if (await decide(open.orgId, open.planId, decision, workerNumber)) {
      setNotice(decidedNotice(decision));
      await onDecided();
    }
  });
  // A Worker or AI question is answered here, not by approving again: each choice is the approval.
  // What the server answers a refused choice with replaces what was drawn before.
  const refused = refusedChoices.length > 0;
  const choices = refused ? refusedChoices : offer.choices ?? [];
  if (notice !== null) return <p className="muted">{notice}</p>;
  // A held draft is answered, tried again or dropped: its tasks are there to open, not to approve.
  const heldDraft = offer.note !== undefined;
  const taskList = offer.tasks.map((task, index) => (
    <li key={index}>
      <span className="task-title">{task.title}</span>
      <p className="instructions">{task.instructions}</p>
    </li>
  ));
  return (
    <div className="stack decision">
      <strong className="offer-summary">{offer.summary}</strong>
      <small className="muted">
        {offer.tasksLabel}{offer.where === null ? '' : ` · ${offer.where}`}
      </small>
      {offer.tasks.length > 0 && (heldDraft
        ? <details><summary>{offer.tasksLabel}</summary><ol className="plan-tasks open">{taskList}</ol></details>
        : <ol className="plan-tasks open">{taskList}</ol>)}
      <HeldDraftChoices choices={choices} prompt={refused ? undefined : offer.choicePrompt} busy={busy}
        buttonClassName="ghost" onChoose={(workerNumber) => act('approve', workerNumber)} />
      {heldDraft
        ? offer.decisions.map((decision) => (
          <div key={decision} className="row">
            <button type="button" className="ghost" disabled={busy}
              onClick={() => act(decision)}>{offer.labels?.[decision] ?? DECISION_LABEL[decision]}</button>
            <small className="muted decision-note">{decision === 'approve' ? offer.retryNote : offer.note}</small>
          </div>
        ))
        : (
          <div className="row">
            {offer.decisions.map((decision) => (
              <button key={decision} type="button" className={decision === 'approve' ? 'primary' : 'ghost'}
                disabled={busy} onClick={() => act(decision)}>{offer.labels?.[decision] ?? DECISION_LABEL[decision]}</button>
            ))}
          </div>
        )}
      {error !== null && <p role="alert">{error}</p>}
    </div>
  );
}

function Outcome({ outcome }: { outcome: PlanOutcome }) {
  const tone = outcome.failed ? 'bad' : outcome.settled ? 'good' : 'going';
  return (
    <div className="outcome">
      <span className={`outcome-head ${tone}`}>{outcome.headline}</span>
      {outcome.tasks.length > 0 && (
        <ol className="plan-tasks open">
          {outcome.tasks.map((task) => (
            <li key={task.taskId}>
              <span className="task-title">{task.title}</span>
              <span className={`outcome-line ${task.state}`}>{task.line}</span>
              {task.detail !== null && <p className="instructions">{task.detail}</p>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A message straight after another from the same speaker joins its group. */
function turnClass(item: SharedTimelineItem, previous: SharedTimelineItem | undefined): string {
  const continued = previous !== undefined && previous.role === item.role && previous.mine === item.mine;
  return ['turn', item.mine ? 'mine' : '', continued ? 'continued' : ''].filter(Boolean).join(' ');
}

function itemBubbleClass(item: SharedTimelineItem): string {
  if (item.authority === 'observer_discussion') return `bubble observer role-${item.role}`;
  if (item.authority === 'worker_chat') return 'bubble worker role-worker';
  const turn = item.commandTurn;
  if (turn === null) return 'bubble';
  if (turn.kind === 'note') return 'bubble note';
  if (turn.kind === 'answer') return 'bubble';
  if (turn.statusLabel === 'archived') return 'bubble';
  if (turn.status === 'failed') return 'bubble failed';
  if (turn.status === 'proposed') return 'bubble waiting';
  return 'bubble';
}
